import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { DateTime } from "luxon"

// Context
import { UserContext } from "../../../context/userContext"

//styling
import assessStyles from "../../../styles/assessments/Assessment.module.css"
import myStyles from "../../../styles/assessments/ParticipantAssessments.module.css"

//PrimeReact
import { Dropdown } from "primereact/dropdown"
import { Button } from "primereact/button"
import { Column } from "primereact/column"
import { DataTable } from "primereact/datatable"
import { Paginator } from "primereact/paginator"
import { ConfirmDialog } from "primereact/confirmdialog" // For <ConfirmDialog /> component
import { confirmDialog } from "primereact/confirmdialog" // For confirmDialog method

//Participant components
import ParticipantAssessmentFilters from "./ParticipantAssessmentsFilters"
import {
  assessmentsApiScheduleDeletePost,
  assessmentsApiScheduleListPost,
} from "../assessmentsApi"

//Helpers
import { LogError } from "../../../helpers/logger"
import { LoaderMedium, LoaderSmall } from "../../../components/Loaders"
import { useLazyQuery } from "@apollo/client"
import { GET_USERS_FROM_LIST } from "../queries"
import isParticipantActive from "../../../helpers/isParticipantActive"
import InactiveParticipantToolTip from "./InactiveParticipantToolTip"

export default function ParticipantAssessmentsSchedules({
  triggerRefresh,
  onEdit,
  onProcessScheduledTask,
}) {
  const userCtx = useContext(UserContext)
  const participantIsActive = isParticipantActive(
    userCtx.participant_membership_status,
  )

  const defaultFilters = {
    sortField: "name",
    sortOrder: "a-z",
    pageSize: 5,
    first: 0,
    pageNum: 1,
  }

  const [isLoading, setIsLoading] = useState(true)
  const [rerender, setRerender] = useState(false)
  const [dataset, setDataset] = useState(undefined)
  const [expandedSchedule, setExpandedSchedule] = useState([])
  const [filters, setFilters] = useState(defaultFilters)
  const [additionalFilters, setAdditionalFilters] = useState({
    includeUpcoming: true,
    includeCompleted: false,
  })
  const [showFilterDialog, setShowFilterDialog] = useState(false)

  const deletingScheduleRef = useRef([])
  const deletedScheduleRef = useRef([])

  const [loadStaffProfiles] = useLazyQuery(GET_USERS_FROM_LIST, {
    fetchPolicy: "cache-and-network",
    skip: true,
    variables: {
      list: [],
    },
  })

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true)

      const useFilters = {
        ...filters,
        organizationID: userCtx.session.user.organization_id,
        participantId: userCtx.participant_id,
        searchPhrase: additionalFilters?.searchPhrase,
        from: additionalFilters?.completedFrom,
        to: additionalFilters?.completedTo,
        includeUpcoming: additionalFilters?.includeUpcoming,
        includeCompleted: additionalFilters?.includeCompleted,
        includeParticipantTasksOnly: false,
        endOfDay: DateTime.now().endOf("day").toUTC().toISO(),
      }

      let results = await assessmentsApiScheduleListPost(
        userCtx.tokenAssessments,
        useFilters,
      )

      //Load staff details if there are assignments allocates to them
      let staffNames = []
      let staff_ids = []

      let staffAssignments = results?.schedules?.filter(
        (schedule) => schedule.assignedTarget !== "participant",
      )
      if (staffAssignments) {
        staff_ids = [
          ...new Set(
            staffAssignments.map((staffId) => staffId.assignedStaffId),
          ),
        ]
      }

      staff_ids = [
        ...staff_ids,
        ...new Set(results?.schedules?.map((staffId) => staffId.createdBy)),
      ]

      const staffResults = await loadStaffProfiles({
        variables: { list: staff_ids },
      })

      if (staffResults.data?.usersFromList) {
        staffNames = staffResults.data?.usersFromList
      } else {
        LogError("Unable to load users from list", staffResults)
      }

      results.schedules = results.schedules.map((schedule) => {
        let assigneeName = `${userCtx.participant_name_first} ${userCtx.participant_name_last}`
        let assignerName = "Unknown"

        if (schedule.assignedTarget !== "participant") {
          const staff = staffNames?.find(
            (staff) => staff.staff_id === schedule.assignedStaffId,
          )
          if (staff) {
            assigneeName = `${staff.staff_full_name}`
          } else {
            assigneeName = "Unknown staff"
          }
        }

        const creator = staffNames?.find(
          (staff) => staff.staff_id === schedule.createdBy,
        )

        if (creator) {
          assignerName = creator.staff_full_name
        }

        const newTasks = schedule.tasks?.map((task) => {
          return { ...task, assigneeName, assignerName }
        })

        return { ...schedule, assigneeName, assignerName, tasks: newTasks }
      })

      setDataset(results)
      setIsLoading(false)
    } catch (error) {
      LogError("Unable to list assignments", error)
      userCtx.setNotificationError("Unable to list assignments")

      setDataset(undefined)
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    triggerRefresh,
    filters,
    additionalFilters,
    userCtx.participantId,
    userCtx.session.user.organization_id,
  ])

  useEffect(() => {
    loadData()
  }, [loadData])

  const deleteSchedule = async (scheduleId) => {
    try {
      deletingScheduleRef.current = [...deletingScheduleRef.current, scheduleId]
      setRerender(!rerender)

      await assessmentsApiScheduleDeletePost(userCtx.tokenAssessments, {
        id: scheduleId,
        userId: userCtx.session.user.staff_id,
      })

      deletedScheduleRef.current = [...deletedScheduleRef.current, scheduleId]
      setRerender(rerender)
    } catch (error) {
      LogError("Unable to delete assignment", error)
      userCtx.setNotificationError("Unable to delete assignment")
    } finally {
      //Remove scheduleId from deleting reference
      deletingScheduleRef.current = deletingScheduleRef.current.filter(
        (id) => id !== scheduleId,
      )

      setRerender(rerender)
    }
  }

  const RenderActionBody = (rowData) => {
    if (deletingScheduleRef.current.includes(rowData.id)) {
      return <LoaderSmall />
    } else {
      return (
        <div className={assessStyles.actionButtonContainer}>
          <InactiveParticipantToolTip>
            <Button
              type="button"
              onClick={() => {
                onEdit(rowData.id)
              }}
              className={assessStyles.actionButtonPurple}
              tooltip="Edit schedule"
              tooltipOptions={{ position: "bottom" }}
              disabled={!participantIsActive}
            >
              <span className={`material-icons`}>{"edit"}</span>
            </Button>
          </InactiveParticipantToolTip>

          <InactiveParticipantToolTip>
            <Button
              type="button"
              onClick={(event) => {
                confirmDialog({
                  message: "Are you sure you want to proceed?",
                  header: "Confirmation",
                  icon: "pi pi-exclamation-triangle",
                  defaultFocus: "accept",
                  accept: () => {
                    deleteSchedule(rowData.id)
                  },
                  reject: () => {},
                })
              }}
              className={assessStyles.actionButtonPurple}
              tooltip="Delete schedule"
              tooltipOptions={{ position: "bottom" }}
              disabled={!participantIsActive}
            >
              <span className={`material-icons`}>{"delete"}</span>
            </Button>
          </InactiveParticipantToolTip>
        </div>
      )
    }
  }

  const RenderDateTemplate = (rowData, fieldName, includeTime, isDateOnly) => {
    let date = rowData[fieldName]

    if (!!date) {
      const dateFromIso = isDateOnly
        ? DateTime.fromISO(date) //For date only values, we want to treat the value as a local date and not convert it to utc first
        : DateTime.fromISO(date, { zone: "utc" })

      date = dateFromIso
        .toLocal()
        .toLocaleString(includeTime ? DateTime.DATETIME_MED : undefined)
    }

    return <>{date}</>
  }

  const allowScheduleExpander = (rowData) => {
    return rowData?.tasks?.length > 0
  }

  const ScheduledTaskAction = (rowData) => {
    const dueDate = DateTime.fromISO(rowData.date)
    const today = DateTime.now()

    let tooltipText = ""
    let canComplete = false

    if (rowData.dateCompleted) {
      tooltipText = "View assessments"
    } else if (dueDate.startOf("day").ts <= today.startOf("day").ts) {
      canComplete = true
      tooltipText = "Complete assessments"
    } else {
      tooltipText = "View upcoming scheduled assessments"
    }

    return (
      <>
        <Button
          type="button"
          onClick={() => {
            onProcessScheduledTask(rowData.id)
          }}
          className={assessStyles.actionButtonPurple}
          tooltip={tooltipText}
          tooltipOptions={{ position: "bottom" }}
        >
          <span className={`material-icons`}>
            {canComplete ? "task" : "visibility"}
          </span>
        </Button>
      </>
    )
  }

  const ExpandedScheduleTemplate = (rowData) => {
    const StatusColumn = (taskRowData) => {
      const dueDate = DateTime.fromISO(taskRowData.date)
      const today = DateTime.now()

      let status = "Upcoming"
      let statusClass = assessStyles.badgePending

      if (taskRowData.dateCompleted) {
        status = "Completed"
        statusClass = assessStyles.badgeCompleted
      } else if (dueDate.hasSame(today, "day")) {
        status = "due"
        statusClass = assessStyles.badgeCompleted
      } else if (dueDate < DateTime.now()) {
        status = "Overdue"
        statusClass = assessStyles.badgePendingRequired
      }

      return (
        <div className={`${assessStyles.badge} ${statusClass}`}>{status}</div>
      )
    }

    return (
      <div className={myStyles.expandedScheduleContainer}>
        <DataTable
          value={rowData.tasks}
          className={`${assessStyles.dataTable} ${myStyles.scheduleTasks}`}
        >
          <Column
            field="date"
            header="Due date"
            body={(rowData) => {
              return RenderDateTemplate(rowData, "date", true)
            }}
          />
          <Column header={"Assigned to"} field="assigneeName" />
          <Column
            field="completedDate"
            header="Completed date"
            body={(rowData) => {
              return RenderDateTemplate(rowData, "dateCompleted", true, true)
            }}
          />
          <Column body={StatusColumn} header="Status" />
          <Column body={ScheduledTaskAction} />
        </DataTable>
      </div>
    )
  }

  // Count the number of active filters for display in the filter button
  const filterCount =
    (additionalFilters?.searchPhrase ? 1 : 0) +
    (additionalFilters?.completedFrom ? 1 : 0) +
    (additionalFilters?.completedTo ? 1 : 0) +
    (additionalFilters?.includeUpcoming !== true ? 1 : 0) +
    (additionalFilters?.includeCompleted === true ? 1 : 0)

  return (
    <>
      {showFilterDialog ? (
        <ParticipantAssessmentFilters
          filters={additionalFilters}
          isScheduled={true}
          onSetFilters={(newFilters) => {
            setAdditionalFilters(newFilters)
            setShowFilterDialog(false)
          }}
          onClose={() => {
            setShowFilterDialog(false)
          }}
        />
      ) : null}
      <ConfirmDialog />
      <div className={myStyles.completedLayout}>
        <div className={myStyles.completedLayoutHeader}></div>
        <div className={myStyles.completedLayoutFilters}>
          <Button
            className={`${myStyles.actionButton}`}
            onClick={() => {
              setShowFilterDialog(true)
            }}
          >
            <span className={`${myStyles.filterButtonText}`}>{`Filters${
              filterCount > 0 ? ` [${filterCount}]` : ""
            }`}</span>
            <span className={`material-icons`}>{"tune"}</span>
          </Button>
        </div>
        <div className={myStyles.completedLayoutSort}>
          <Dropdown
            className={myStyles.completedComponent}
            value={filters.sortField}
            options={[
              { label: "Sort by: Name", value: "name" },
              { label: "Sort by: Schedule type", value: "type" },
              { label: "Sort by: Start date", value: "startDate" },
              { label: "Sort by: End date", value: "endDate" },
            ]}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => {
              setFilters({ ...filters, sortField: e.value })
            }}
          />
        </div>
        <div className={myStyles.completedLayoutBody}>
          {isLoading ? (
            <LoaderMedium />
          ) : (
            <div className={myStyles.scheduleListTableContainer}>
              <DataTable
                value={
                  dataset?.schedules?.filter(
                    (entry) => !deletedScheduleRef.current.includes(entry.id),
                  ) || []
                }
                sortField={filters.sortField}
                sortOrder={filters.sortOrder === "a-z" ? 1 : -1}
                onSort={(sort) => {
                  const sortOrder = sort.sortOrder === 1 ? "a-z" : "z-a"
                  setFilters({
                    ...filters,
                    sortField: sort.sortField,
                    sortOrder: sortOrder,
                  })
                }}
                expandedRows={expandedSchedule}
                rowExpansionTemplate={ExpandedScheduleTemplate}
                onRowToggle={(e) => {
                  setExpandedSchedule(e.data)
                }}
                tableClassName={`${assessStyles.dataTable}`}
              >
                <Column
                  expander={(rowData) => {
                    allowScheduleExpander(rowData)
                  }}
                  className={myStyles.expanderColumn}
                />
                <Column header={"Name"} field={"name"} sortable></Column>
                <Column header={"Assigned by"} field="assignerName" />
                <Column header={"Assigned to"} field="assigneeName" />
                <Column
                  header={"Scheduled type"}
                  field={"scheduleType"}
                  sortable
                ></Column>
                <Column
                  header={"Start date"}
                  field={"startDate"}
                  sortable
                  body={(rowData) => {
                    return RenderDateTemplate(rowData, "startDate")
                  }}
                ></Column>
                <Column
                  header={"End date"}
                  field={"endDate"}
                  sortable
                  body={(rowData) => {
                    return RenderDateTemplate(rowData, "endDate")
                  }}
                ></Column>
                <Column body={RenderActionBody} />
              </DataTable>
              <Paginator
                first={dataset ? (dataset?.pageNum - 1) * dataset?.pageSize : 0}
                rows={dataset?.pageSize || 0}
                totalRecords={dataset?.numTotal || 0}
                rowsPerPageOptions={[5, 10, 20, 30]}
                onPageChange={(setup) => {
                  setFilters({
                    ...filters,
                    pageSize: setup.rows,
                    first: setup.first,
                    pageNum: setup.page + 1,
                  })
                }}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
